// Calendar arithmetic for the handover schedule, in the user's local time. Weeks start on Sunday.
import type { Appointment, AppointmentStatus, HandoverType } from '@sivan/shared';

export type CalendarView = 'day' | 'week' | 'month';

export const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'] as const;
export const MONTH_NAMES = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
] as const;

const pad = (n: number) => String(n).padStart(2, '0');

export const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d: Date, days: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
export const startOfWeek = (d: Date): Date => addDays(startOfDay(d), -d.getDay());

/** YYYY-MM-DD of a local date. */
export const dayKey = (d: Date): string =>
  `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseDayKey = (key: string): Date => {
  const [y = 1970, m = 1, d = 1] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** The days shown, and the [from, to) range to load: one day, a week, or six weeks for a month. */
export function rangeFor(view: CalendarView, anchor: Date): { days: Date[]; from: Date; to: Date } {
  const first =
    view === 'day'
      ? startOfDay(anchor)
      : view === 'week'
        ? startOfWeek(anchor)
        : startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const count = view === 'day' ? 1 : view === 'week' ? 7 : 42;
  const days = Array.from({ length: count }, (_, i) => addDays(first, i));
  return { days, from: first, to: addDays(first, count) };
}

/** The anchor after moving one view length back (-1) or forward (+1). */
export function step(view: CalendarView, anchor: Date, delta: -1 | 1): Date {
  if (view === 'day') return addDays(anchor, delta);
  if (view === 'week') return addDays(anchor, 7 * delta);
  return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
}

export function title(view: CalendarView, anchor: Date): string {
  if (view === 'month')
    return `${MONTH_NAMES[anchor.getMonth()] ?? ''} ${String(anchor.getFullYear())}`;
  if (view === 'day')
    return `יום ${DAY_NAMES[anchor.getDay()] ?? ''}, ${String(anchor.getDate())} ב${MONTH_NAMES[anchor.getMonth()] ?? ''} ${String(anchor.getFullYear())}`;
  const { days } = rangeFor('week', anchor);
  const a = days[0] ?? anchor;
  const b = days[6] ?? anchor;
  return `${String(a.getDate())}/${String(a.getMonth() + 1)} – ${String(b.getDate())}/${String(b.getMonth() + 1)}/${String(b.getFullYear())}`;
}

/** 14:05 */
export const timeOf = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const endTimeOf = (iso: string, minutes: number): string =>
  timeOf(new Date(Date.parse(iso) + minutes * 60_000).toISOString());
/** 15/01/2030 */
export const dateOf = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear())}`;
};
/** A local date (YYYY-MM-DD) and time (HH:MM) as an ISO instant. */
export const toInstant = (day: string, time: string): string =>
  new Date(`${day}T${time}:00`).toISOString();

export interface Filters {
  readonly type: HandoverType | 'all';
  readonly status: AppointmentStatus | 'active' | 'all';
  readonly building: string;
  /** 'mine': appointments with an open request or assignment of mine. */
  readonly who: 'all' | 'mine' | 'unassigned' | 'pending';
}

export const DEFAULT_FILTERS: Filters = { type: 'all', status: 'active', building: '', who: 'all' };

export const primaryOf = (a: Appointment) =>
  a.assignments.find((s) => s.role === 'primary' && s.status === 'assigned');
export const pendingOf = (a: Appointment) => a.assignments.filter((s) => s.status === 'requested');

export function applyFilters(
  list: readonly Appointment[],
  filters: Filters,
  me: string | null,
): Appointment[] {
  return list.filter((a) => {
    if (filters.type !== 'all' && a.type !== filters.type) return false;
    if (filters.status === 'active' && !['planned', 'confirmed'].includes(a.status)) return false;
    if (filters.status !== 'active' && filters.status !== 'all' && a.status !== filters.status)
      return false;
    if (filters.building !== '' && a.apartment.building !== filters.building) return false;
    if (filters.who === 'mine')
      return a.assignments.some(
        (s) => s.employee.id === me && (s.status === 'assigned' || s.status === 'requested'),
      );
    if (filters.who === 'unassigned') return primaryOf(a) === undefined;
    if (filters.who === 'pending') return pendingOf(a).length > 0;
    return true;
  });
}

/** Appointments grouped by local day key, in time order. */
export function byDay(list: readonly Appointment[]): Map<string, Appointment[]> {
  const map = new Map<string, Appointment[]>();
  for (const a of [...list].sort((x, y) => Date.parse(x.startsAt) - Date.parse(y.startsAt))) {
    const key = dayKey(new Date(a.startsAt));
    map.set(key, [...(map.get(key) ?? []), a]);
  }
  return map;
}
